import { NetworkStatus, gql, useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { NoPeople } from '@/components/NoPeople';
import { Pagination } from '@/components/Pagination';
import { Search } from '@/components/Search';
import { useGroupPermissions } from '@/hooks/useGroupPermissions';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import {
  DownloadIcon,
  MoreHorizontalIcon,
  UserPlusIcon,
} from '@/imports/ui/chakra/feather';
import { useDebounce } from '@/imports/ui/hooks/useDebounce';

import { GroupUserSortDirection, GroupUserSortField } from '@/graphql-types/globalTypes';

import { GroupMembers_ExportGroupMembers } from './graphql-types/GroupMembers_ExportGroupMembers';
import {
  GroupMembers_GroupUsersSearch,
  GroupMembers_GroupUsersSearchVariables,
} from './graphql-types/GroupMembers_GroupUsersSearch';

import { ExportInProgressModal } from '../admin/ExportInProgressModal';
import { GroupAddMembersModal } from './components/GroupAddMembersModal';
import { GroupUserCsvImportModal } from './components/GroupUserImportModal/GroupUserImportModal';
import { Member } from './components/Member';
import {
  DEFAULT_MEMBERS_SORT,
  MembersSortDropdown,
  MembersSortValue,
} from './components/MembersSortDropdown';

const GROUP_USER_SEARCH_QUERY = gql`
  query GroupMembers_GroupUsersSearch($input: GroupUserSearchInput!) {
    groupUserSearch(input: $input) {
      results {
        ...GroupMembersPaneMember_UserFragment
      }
      totalCount
    }
  }
  ${Member.userFragment}
`;

const EXPORT_GROUP_MEMBERS = gql`
  mutation GroupMembers_ExportGroupMembers($input: ExportGroupMembersInput!) {
    exportGroupMembers(input: $input) {
      success
    }
  }
`;

type IGroupMemberPageContentProps = {
  groupId: string;
  getUserLink: (userId: string) => string;
};

export const GroupMembers: React.FC<IGroupMemberPageContentProps> = ({
  groupId,
  getUserLink,
}) => {
  const addGroupMemberDisclosure = useDisclosure();
  const bulkImportModalDisclosure = useDisclosure();

  const userGroupPermissions = useGroupPermissions(groupId);

  const groupsMembersSuccessDisclosure = useDisclosure();
  const toast = useToast();

  const [executeExportGroupMembers, executeExportGroupMembersResults] =
    useMutation<GroupMembers_ExportGroupMembers>(EXPORT_GROUP_MEMBERS);

  const handleExecuteExportGroupMembers = async (): Promise<void> => {
    try {
      await executeExportGroupMembers({
        variables: {
          input: {
            groupIds: [groupId],
          },
        },
      });
      groupsMembersSuccessDisclosure.onOpen();
    } catch (err) {
      toast({
        title: `Failed to export group members. Please try again.`,
        status: 'error',
      });
    }
  };

  const limit = 50;

  const router = useRouter();

  const searchText = router.query.searchText
    ? router.query.searchText.toString()
    : undefined;
  const offset = router.query.offset ? Number(router.query.offset) : 0;
  const canSortMembers =
    !userGroupPermissions.isLoading &&
    userGroupPermissions.canEditMembership;
  const sortValue: MembersSortValue =
    router.query.sort === 'name-desc'
      ? 'name-desc'
      : router.query.sort === 'name-asc'
      ? 'name-asc'
      : DEFAULT_MEMBERS_SORT;

  const pendingSearchLoadRef = React.useRef(false);
  const pendingSortLoadRef = React.useRef(false);
  const wasUpdatingVariablesRef = React.useRef(false);

  const setSearchText = useDebounce((nextSearchText: string) => {
    pendingSearchLoadRef.current = true;
    pendingSortLoadRef.current = false;
    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, searchText: nextSearchText, offset: 0 },
      },
      undefined,
      { scroll: false }
    );
  }, 500);

  const setOffset = (value: number) => {
    // Pagination only — do not show search/sort control spinners.
    pendingSearchLoadRef.current = false;
    pendingSortLoadRef.current = false;
    router.replace({
      pathname: router.pathname,
      query: { ...router.query, offset: value },
    });
  };

  const setSortValue = (value: MembersSortValue) => {
    pendingSortLoadRef.current = true;
    pendingSearchLoadRef.current = false;
    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, sort: value, offset: 0 },
      },
      undefined,
      { scroll: false }
    );
  };

  const query = useQuery<
    GroupMembers_GroupUsersSearch,
    GroupMembers_GroupUsersSearchVariables
  >(GROUP_USER_SEARCH_QUERY, {
    skip: userGroupPermissions.isLoading,
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        offset: offset * limit,
        limit,
        filter: {
          groupId,
          searchText: searchText,
        },
        ...(canSortMembers
          ? sortValue === 'default'
            ? {
                sortField: GroupUserSortField.MANUAL,
              }
            : {
                sortField: GroupUserSortField.NAME,
                sortDirection:
                  sortValue === 'name-desc'
                    ? GroupUserSortDirection.DESC
                    : GroupUserSortDirection.ASC,
              }
          : {}),
      },
    },
  });

  const isUpdatingVariables =
    query.networkStatus === NetworkStatus.setVariables;

  React.useEffect(() => {
    if (wasUpdatingVariablesRef.current && !isUpdatingVariables) {
      pendingSearchLoadRef.current = false;
      pendingSortLoadRef.current = false;
    }
    wasUpdatingVariablesRef.current = isUpdatingVariables;
  }, [isUpdatingVariables]);

  // Intent-based: only the control that triggered the fetch shows a spinner.
  // Value-diff refs raced with Apollo/router and could flash the sort spinner
  // on some pagination clicks (e.g. page 12).
  const isSearchLoading =
    isUpdatingVariables && pendingSearchLoadRef.current;
  const isSortLoading = isUpdatingVariables && pendingSortLoadRef.current;

  const groupUserSearch =
    query.data?.groupUserSearch || query.previousData?.groupUserSearch;

  const numMembers = groupUserSearch ? groupUserSearch.totalCount : 0;

  return (
    <Stack spacing="4">
      <Card px="6" py="4">
        <Stack spacing="4">
          <Stack direction="row" spacing="4">
            <Box flex="1" minW="0">
              <Search
                defaultValue={searchText}
                onChange={setSearchText}
                isLoading={isSearchLoading}
                label="Search People"
              />
            </Box>
            {canSortMembers && (
              <MembersSortDropdown
                value={sortValue}
                onChange={setSortValue}
                isLoading={isSortLoading}
              />
            )}

            {userGroupPermissions.isLoading ||
            !userGroupPermissions.canEditMembership ? null : (
              <>
                <GroupAddMembersModal
                  isOpen={addGroupMemberDisclosure.isOpen}
                  onClose={addGroupMemberDisclosure.onClose}
                  groupId={groupId}
                />
                <Button
                  aria-label="Add Users To Group"
                  variant="solid"
                  size="md"
                  colorScheme="primary"
                  onClick={addGroupMemberDisclosure.onOpen}
                >
                  Add
                </Button>
                <Menu>
                  <MenuButton
                    size="md"
                    as={IconButton}
                    variant="outline"
                    colorScheme="gray"
                    aria-label="membership actions"
                    icon={<MoreHorizontalIcon />}
                  />
                  <MenuList>
                    <MenuItem
                      icon={<UserPlusIcon />}
                      onClick={bulkImportModalDisclosure.onOpen}
                    >
                      Bulk Import
                    </MenuItem>
                    <GroupUserCsvImportModal
                      isOpen={bulkImportModalDisclosure.isOpen}
                      onClose={bulkImportModalDisclosure.onClose}
                      groupId={groupId}
                    />

                    <MenuItem
                      icon={<DownloadIcon />}
                      onClick={handleExecuteExportGroupMembers}
                      isDisabled={executeExportGroupMembersResults.loading}
                    >
                      Download
                    </MenuItem>
                    <ExportInProgressModal
                      isOpen={groupsMembersSuccessDisclosure.isOpen}
                      onClose={groupsMembersSuccessDisclosure.onClose}
                      title="Your Groups User Export is in Progress"
                    />
                  </MenuList>
                </Menu>
              </>
            )}
          </Stack>
          <Text color="gray.500" textStyle="compact">
            {numMembers} Member{numMembers > 1 || numMembers === 0 ? 's' : ''}{' '}
            (active)
          </Text>
        </Stack>
      </Card>

      <Card overflow="hidden">
        <TableContainer overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                {!userGroupPermissions.isLoading &&
                  userGroupPermissions.canEditMembership && <Th></Th>}
              </Tr>
            </Thead>
            <Tbody>
              {query.networkStatus === NetworkStatus.loading ||
              query.networkStatus === NetworkStatus.setVariables ||
              userGroupPermissions.isLoading ? (
                <Tr>
                  <Td colSpan={100}>
                    <Loading />
                  </Td>
                </Tr>
              ) : query.error ? (
                <Tr>
                  <Td colSpan={100}>
                    <UnexpectedError />
                  </Td>
                </Tr>
              ) : groupUserSearch && groupUserSearch.results.length ? (
                groupUserSearch.results.map((user) => (
                  <Member
                    key={user._id}
                    member={user}
                    groupId={groupId}
                    getUserLink={getUserLink}
                  />
                ))
              ) : (
                <Tr>
                  <Td colSpan={100}>
                    <NoPeople
                      isSearchResult={
                        !!query.variables?.input.filter?.searchText
                      }
                    />
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Card>

      {groupUserSearch && groupUserSearch.totalCount && (
        <Pagination
          currentPage={offset + 1}
          numberOfPages={Math.ceil(groupUserSearch.totalCount / limit)}
          onChange={(value) => {
            setOffset(value - 1);
          }}
        />
      )}
    </Stack>
  );
};
